import { buildCPForm52OfMRCData } from "./downloadCPForm52OfMRCPdf";
import { dummyAdvancePaymentBillData } from "../../../components/ElectricityConsumerBill/dummyAdvancePaymentBillData";
import { getDiscoBillBranding } from "../../../components/ElectricityConsumerBill/discoBillBranding";
import { computeMrcBillPayableAmount } from "../../../components/ElectricityConsumerBill/fetchMrcBillPaymentQr";
import { addBusinessDays } from "../../../services/Functions";
import {
  calcUnitsCharged,
  formatUnitsAdjDisplay,
  getCp52MeterReadingRows,
  hasMrcPresentReading,
  isMdiMeterRow,
  isDisplayableMeterImageSrc,
  isPdfMeterSrc,
  mapDocumentsToReadingRows,
  METER_ID_KWH,
  METER_ID_KVARH,
  METER_ID_MDI,
  resolveCp52MeterToBeReading,
  resolveMrcCategoryFromTariff,
  resolveMrcTariffCode,
  resolveMrcTariffDisplay,
} from "./mrcReadingUtils";
import { pdfToImages } from "../../../utils/pdfToImages";

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const pick = (obj, keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const asText = (value) => {
  if (value === undefined || value === null || value === "") return "";
  return String(value);
};

/** Format API amount for CP52 cells (null → 0). */
export const formatCp52Amount = (value) => {
  if (value === undefined || value === null || value === "") return "0";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(2)));
};

/** Oracle/ISO date → 10-AUG-26 */
export const formatCp52Date = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  // Already display-like
  if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(raw)) return raw.toUpperCase();

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()] || "";
  const yy = String(d.getFullYear()).slice(-2);
  return `${day}-${mon}-${yy}`;
};

/** BILL MONTH on MRC — JUL-26 only (never a calendar day). */
export const formatCp52BillingMonth = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value)
    .trim()
    .toUpperCase()
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
  if (!raw) return "";

  const monthYear = raw.match(
    /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[-\s/.]*(\d{2}|\d{4})/
  );
  if (monthYear) {
    return `${monthYear[1]}-${monthYear[2].slice(-2)}`;
  }

  const iso = raw.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?/);
  if (iso) {
    const mon = MONTHS[Number(iso[2]) - 1];
    if (mon) return `${mon}-${iso[1].slice(-2)}`;
  }

  const yyyymm = raw.match(/^(\d{4})(\d{2})(?:\d{2})?$/);
  if (yyyymm) {
    const mon = MONTHS[Number(yyyymm[2]) - 1];
    if (mon) return `${mon}-${yyyymm[1].slice(-2)}`;
  }

  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const mon = MONTHS[d.getMonth()] || "";
    const yy = String(d.getFullYear()).slice(-2);
    return mon ? `${mon}-${yy}` : "";
  }

  return "";
};

/** Parse bill due date (DD-MON-YY / ISO) → Date, or null. */
const parseBillDueDate = (value) => {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const direct = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (direct) {
    const day = Number(direct[1]);
    const mon = MONTHS.indexOf(direct[2].toUpperCase());
    let year = Number(direct[3]);
    if (year < 100) year += 2000;
    if (mon < 0) return null;
    const d = new Date(year, mon, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Due Date module style: due date + 3 working days (weekends/holidays skipped).
 * Used for L.P. surcharge "Till / After" window on MRC bill.
 */
const getMrcSurchargeWindowDate = (dueDateValue) => {
  const start = parseBillDueDate(dueDateValue);
  if (!start) return "";
  return formatCp52Date(addBusinessDays(start, 3));
};

/** All CP52 meter sets → bill METER INFO rows (set-wise like prepare dialog). */
export const buildMrcBillMeterInfoRows = (documents = [], formData = {}) => {
  const rows = getCp52MeterReadingRows(documents, formData);
  return rows
    .map((row, index) => {
      const previous =
        row.previous != null && row.previous !== ""
          ? String(row.previous)
          : index === 0 && formData.previousReading
            ? String(formData.previousReading)
            : "";
      const presentRaw = resolveCp52MeterToBeReading(row, formData);
      const present =
        presentRaw != null && presentRaw !== ""
          ? String(presentRaw)
          : "";
      const hasPresent = hasMrcPresentReading(presentRaw);
      const previousNum = Number(String(previous).replace(/,/g, "").trim());
      const presentNum = Number(String(present).replace(/,/g, "").trim());
      const mfNum = Number(
        String(row.mf ?? row.meterFactor ?? "1").replace(/,/g, "").trim()
      );
      const mfFactor = Number.isFinite(mfNum) && mfNum !== 0 ? mfNum : 1;
      // No Present reading → do not calculate Units (empty Present must not become 0).
      const unitsFromReadings =
        hasPresent && Number.isFinite(presentNum) && Number.isFinite(previousNum)
          ? Number(((presentNum - previousNum) * mfFactor).toFixed(2))
          : "";
      const unitsCharged = calcUnitsCharged(
        row.asCharged,
        row.previous,
        row.mf ?? row.meterFactor
      );
      const unitsChargedRounded =
        unitsCharged != null ? Math.round(unitsCharged) : null;
      const units =
        !isMdiMeterRow(row) && hasPresent && unitsFromReadings !== ""
          ? String(unitsFromReadings)
          : "";

      return {
        id: row.id || `${row.setNo}-${row.meterId}-${index}`,
        setNo: row.setNo != null && row.setNo !== "" ? String(row.setNo) : "",
        meterId: Number(row.meterId) || 0,
        meterLabel: row.meterLabel || "",
        meterNo: row.meterSerialNo || formData.meterNo || "",
        meterFactor:
          row.mf != null && row.mf !== ""
            ? String(row.mf)
            : formData.mf || formData.meterFactor || "1",
        previousReading: previous || null,
        presentReading: isMdiMeterRow(row)
          ? row.asCharged != null
            ? String(row.asCharged)
            : present || null
          : present || null,
        units: isMdiMeterRow(row)
          ? unitsChargedRounded != null
            ? String(unitsChargedRounded)
            : null
          : units || null,
        isMdi: isMdiMeterRow(row),
        asCharged: row.asCharged != null ? String(row.asCharged) : null,
        unitsCharged:
          unitsChargedRounded != null ? String(unitsChargedRounded) : null,
      };
    })
    .sort((a, b) => {
      const setDiff = Number(a.setNo || 0) - Number(b.setNo || 0);
      if (setDiff !== 0) return setDiff;
      return String(a.meterLabel || "").localeCompare(String(b.meterLabel || ""));
    });
};

const isPeakMeterLabel = (label) => {
  const t = String(label || "").toLowerCase();
  return t.includes("peak") && !t.includes("off");
};

const isOffPeakMeterLabel = (label) => {
  const t = String(label || "").toLowerCase();
  return t.includes("off");
};

const displayMeterVal = (value) => {
  if (value == null || value === "") return "—";
  const n = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return String(value);
  // Keep integers clean; decimals → max 2 places (avoids 1.0899999999999999)
  if (Number.isInteger(n)) return String(n);
  return String(Number(n.toFixed(2)));
};

/** Net-meter Import/Export tag: I or E */
const getImportExportTag = (row) => {
  const label = String(row?.meterLabel || "").toLowerCase();
  if (label.includes("export")) return "E";
  if (label.includes("import")) return "I";
  const setNo = Number(row?.setNo);
  // Sets 1–2 = Import, 3–4 = Export (net meter)
  if (Number.isFinite(setNo) && setNo >= 3) return "E";
  if (Number.isFinite(setNo) && setNo >= 1) return "I";
  return "";
};

const withImportExportPrefix = (row, value, { forceNet = false } = {}) => {
  const display = displayMeterVal(value);
  if (!forceNet || display === "—") return display;
  const tag = getImportExportTag(row);
  return tag ? `${tag} ${display}` : display;
};

/**
 * Bill meter photos (KWH only) — one slot per KWH set (Off Peak + Peak).
 * Prefer site evidence on that row; else consumer claimed image.
 * Do not drop a set just because its URL matches another set.
 */
export const buildMrcBillMeterImages = (documents = []) => {
  const kwhRows = mapDocumentsToReadingRows(documents)
    .filter((row) => {
      const id = Number(row.meterId);
      if (id === METER_ID_KVARH || id === METER_ID_MDI) return false;
      if (id === METER_ID_KWH) return true;
      return /kwh/i.test(String(row.meterLabel || ""));
    })
    .sort((a, b) => Number(a.setNo || 0) - Number(b.setNo || 0));

  if (!kwhRows.length) return { urls: [], pdfUrls: [], source: null };

  const urls = [];
  const pdfUrls = [];
  let usedSite = false;
  let usedConsumer = false;

  kwhRows.forEach((row) => {
    const src = row.siteSurveyImageSrc || row.imageSrc || "";
    if (!src) return;
    if (row.siteSurveyImageSrc) usedSite = true;
    else usedConsumer = true;
    if (isPdfMeterSrc(src)) pdfUrls.push(src);
    else if (isDisplayableMeterImageSrc(src)) urls.push(src);
  });

  return {
    urls,
    pdfUrls,
    source: urls.length || pdfUrls.length
      ? usedSite && !usedConsumer
        ? "siteEvidence"
        : usedSite
          ? "mixed"
          : "consumer"
      : null,
  };
};

/** Convert any PDF meter evidence into PNG data URLs for the bill image grid. */
export const expandMrcBillMeterImages = async (mapped = {}) => {
  const images = [];
  const push = (src) => {
    const s = String(src || "").trim();
    if (!s) return;
    images.push(s);
  };

  const inputs = [
    ...(Array.isArray(mapped.meterImageUrls) ? mapped.meterImageUrls : []),
    ...(Array.isArray(mapped.meterImagePdfs) ? mapped.meterImagePdfs : []),
  ].filter(Boolean);

  for (const src of inputs) {
    if (images.length >= 4) break;
    if (isPdfMeterSrc(src)) {
      const pages = await pdfToImages(src);
      pages.forEach(push);
    } else if (isDisplayableMeterImageSrc(src)) {
      push(src);
    }
  }

  return {
    ...mapped,
    meterImageUrls: images.slice(0, 4),
    meterImageUrl: images[0] || null,
    meterImagePdfs: [],
  };
};

/**
 * FESCO-style METER INFO table model:
 * METER | TYPE | PREVIOUS | PRESENT | MF | UNITS
 * Values stack dynamically for every set (1..N) — not limited to Off/Peak only.
 */
export const buildMrcBillMeterInfoTable = (documents = [], formData = {}) => {
  const rows = buildMrcBillMeterInfoRows(documents, formData);
  if (!rows.length) return null;

  const meterNo =
    rows.find((r) => r.meterNo)?.meterNo || formData.meterNo || "";

  const kwhRows = rows.filter(
    (r) => Number(r.meterId) === 1 || /kwh/i.test(r.meterLabel)
  );
  const kvarhRows = rows.filter(
    (r) => Number(r.meterId) === 2 || /kvarh/i.test(r.meterLabel)
  );
  const mdiRows = rows.filter((r) => r.isMdi || Number(r.meterId) === 3);

  // Net meter when Import/Export labels or sets 3/4 exist
  const isNetMeter =
    rows.some((r) => /import|export/i.test(String(r.meterLabel || ""))) ||
    rows.some((r) => Number(r.setNo) >= 3);

  const sortBySet = (list) =>
    [...list].sort((a, b) => {
      const setDiff = Number(a.setNo || 0) - Number(b.setNo || 0);
      if (setDiff !== 0) return setDiff;
      // Off Peak before Peak within same set
      const aPeak = isPeakMeterLabel(a.meterLabel) ? 1 : 0;
      const bPeak = isPeakMeterLabel(b.meterLabel) ? 1 : 0;
      return aPeak - bPeak;
    });

  const unitsPrefixForRow = (row) => {
    // (O)/(P) only for net-meter bills — never on simple meters
    if (!isNetMeter) return "";
    if (isOffPeakMeterLabel(row.meterLabel)) return "(O)";
    if (isPeakMeterLabel(row.meterLabel)) return "(P)";
    // Net-meter sets: odd = Off Peak (O), even = Peak (P)
    const setNo = Number(row.setNo);
    if (Number.isFinite(setNo) && setNo > 0) {
      return setNo % 2 === 1 ? "(O)" : "(P)";
    }
    return "";
  };

  const unitsLabelForRow = (row) => {
    const u = hasMrcPresentReading(row.presentReading)
      ? displayMeterVal(row.units)
      : "—";
    const prefix = unitsPrefixForRow(row);
    if (u === "—") return u;
    return prefix ? `${prefix} ${u}` : u;
  };

  const buildTypeRow = (type, list, { mdiMode = false } = {}) => {
    if (!list.length) return null;
    const sorted = sortBySet(list);
    const previous = [];
    const present = [];
    const mf = [];
    const units = [];

    sorted.forEach((row) => {
      // MDI does not change — PREVIOUS + AS CHARGED in PRESENT; UNITS blank.
      // Net meter: prefix readings with I (Import) / E (Export).
      if (mdiMode) {
        previous.push(
          withImportExportPrefix(row, row.previousReading, {
            forceNet: isNetMeter,
          })
        );
        present.push(
          withImportExportPrefix(
            row,
            row.asCharged || row.presentReading,
            { forceNet: isNetMeter }
          )
        );
        units.push("—");
      } else {
        previous.push(
          withImportExportPrefix(row, row.previousReading, {
            forceNet: isNetMeter,
          })
        );
        present.push(
          withImportExportPrefix(row, row.presentReading, {
            forceNet: isNetMeter,
          })
        );
        units.push(unitsLabelForRow(row));
      }
      mf.push(displayMeterVal(row.meterFactor));
    });

    return {
      type,
      previous,
      present,
      mf,
      units,
      unitsDisplay: units.join(" "),
    };
  };

  const typeRows = [
    buildTypeRow("KWH", kwhRows),
    buildTypeRow("KVARH", kvarhRows),
    buildTypeRow("MDI", mdiRows, { mdiMode: true }),
  ].filter(Boolean);

  // METER column: I/E + meter no per set (net meter)
  const meterStackSource = sortBySet(
    kwhRows.length ? kwhRows : kvarhRows.length ? kvarhRows : mdiRows
  );
  const meterNos = meterStackSource.map((row) => {
    const no = row.meterNo || meterNo || "—";
    if (!isNetMeter) return no;
    const tag = getImportExportTag(row);
    return tag ? `${tag} ${no}` : no;
  });

  return {
    meterNo,
    meterNos,
    isNetMeter,
    typeRows,
  };
};

/** REFERENCE_NO e.g. 09131420411763 → batch / subdiv / account */
export const parseCp52ReferenceNo = (referenceNo) => {
  const ref = String(referenceNo || "").replace(/\D/g, "");
  if (ref.length < 9) {
    return { batchNo: "", subDivision: "", accountNo: "" };
  }
  return {
    batchNo: ref.slice(0, 2),
    subDivision: ref.slice(2, 7),
    accountNo: ref.slice(7),
  };
};

/** Unwrap getCP52 payload — data may be object or array. */
export const unwrapMrcCp52Record = (apiResult) => {
  if (!apiResult || typeof apiResult !== "object") return {};
  let root = apiResult.data ?? apiResult.result ?? apiResult.cp52 ?? apiResult;
  if (Array.isArray(root)) root = root[0] ?? {};
  if (root && typeof root === "object") {
    const nested =
      root.cp52Data ??
      root.cp52 ??
      root.adjustmentNote ??
      root.cpForm52 ??
      root.data;
    if (Array.isArray(nested)) return nested[0] ?? root;
    if (nested && typeof nested === "object") return nested;
  }
  return root && typeof root === "object" ? root : {};
};

/** Pull oracle tracking id from prepareCP52 / row / getCP52 payloads. */
export const extractMrcOracleTrackingId = (...sources) => {
  for (const source of sources) {
    if (source == null || source === "") continue;
    if (typeof source !== "object") {
      const text = asText(source).trim();
      if (text) return text;
      continue;
    }
    const record = unwrapMrcCp52Record(source);
    const nested = source.data ?? source.result ?? source;
    const found = pick(record, [
      "oracleTrackingId",
      "OracleTrackingId",
      "ORACLE_TRACKING_ID",
      "TRACKING_ID",
      "RO_ADJ_NO",
      "ADJUSTMENT_NO",
      "billAdjTrackingId",
      "billADJTrackingId",
      "BillAdjTrackingId",
      "trackingId",
      "adjNumber",
      "ADJ_NO",
      "adjNo",
      "adjustmentNoteNo",
    ]) ??
      pick(Array.isArray(nested) ? nested[0] : nested, [
        "oracleTrackingId",
        "TRACKING_ID",
        "RO_ADJ_NO",
        "ADJUSTMENT_NO",
        "billAdjTrackingId",
        "trackingId",
      ]);
    if (found != null && String(found).trim() !== "") {
      return String(found).trim().replace(/\.0$/, "");
    }
  }
  return "";
};

/**
 * Charge column keys on getCP52:
 *   *_C = Amount Charged
 *   *_A = Amount to be Charged
 *   *   = Amount to be Adjusted
 */
const CHARGE_FIELD_KEYS = [
  ["STAX", "STAX"], // Sales Tax
  ["GST", "GST"], // G.S.T
  ["ED", "ED"], // Electricity Duty
  ["NJSUR_CHRG", "NJSUR_CHRG"], // NJ Surcharge
  ["METER_RENT", "METER_RENT"], // Meter Rent
  ["GST_SUBSIDY", "SUBSIDY"], // G.S.T Subsidy
  ["SRV_RENT", "SRV_RENT"], // Service Rent
  ["ENERGY_CHRG", "ENERGY_CHRG"], // Variable Charges
  ["FIX_CHRG", "FIX_CHRG"], // Fixed/Min. Charges
  ["LPF_PENALTY", "LPF_PENALTY"], // LPF Penalty
  ["SEASON_CHRG", "SEASON_CHRG"], // Seasonal Charges
  ["IT", "IT"], // With Holding / Income Tax
  ["EQSUR_CHRG", "EQSUR_CHRG"], // EQ Surcharge
  ["LP_SUR_CHRG", "LP_SUR_CHRG"], // LP Surcharge
  ["ETAX", "ET"], // Etax (ET_C / ET_A / ETAX)
  ["FTAX", "FT"], // Ftax (FT_C / FT_A / FTAX)
];

const readChargeTriplet = (record, primaryKey, altKey) => {
  const charged =
    record[`${primaryKey}_C`] ??
    record[`${altKey}_C`] ??
    record[`${primaryKey}_CHRG_C`];
  const toBeCharged =
    record[`${primaryKey}_A`] ??
    record[`${altKey}_A`] ??
    record[`${primaryKey}_CHRG_A`];
  const adjusted =
    record[primaryKey] ??
    record[altKey] ??
    record[`${primaryKey}_CHRG`];

  // Etax/Ftax special: ETAX/FTAX for adjusted, ET_C/FT_C for charged
  if (primaryKey === "ETAX") {
    return {
      charged: record.ET_C ?? record.ETAX_C ?? charged,
      toBeCharged: record.ET_A ?? record.ETAX_A ?? toBeCharged,
      adjusted: record.ETAX ?? record.ET ?? adjusted,
    };
  }
  if (primaryKey === "FTAX") {
    return {
      charged: record.FT_C ?? record.FTAX_C ?? charged,
      toBeCharged: record.FT_A ?? record.FTAX_A ?? toBeCharged,
      adjusted: record.FTAX ?? record.FT ?? adjusted,
    };
  }

  return { charged, toBeCharged, adjusted };
};

const buildChargeRowsFromRecord = (record, chargeCols) => {
  const amountCharged = [];
  const amountToBeCharged = [];
  const amountToBeAdjusted = [];

  CHARGE_FIELD_KEYS.forEach(([primary, alt], idx) => {
    if (idx >= chargeCols.length) return;
    const { charged, toBeCharged, adjusted } = readChargeTriplet(
      record,
      primary,
      alt
    );
    amountCharged.push(formatCp52Amount(charged));
    amountToBeCharged.push(formatCp52Amount(toBeCharged));
    amountToBeAdjusted.push(formatCp52Amount(adjusted));
  });

  // Pad if chargeCols longer
  while (amountCharged.length < chargeCols.length) {
    amountCharged.push("0");
    amountToBeCharged.push("0");
    amountToBeAdjusted.push("0");
  }

  return { amountCharged, amountToBeCharged, amountToBeAdjusted };
};

/**
 * Map mrc/getCP52 response into CP Form 52 display/PDF data shape.
 * API: *_C = charged, *_A = to be charged, base key = adjusted.
 */
export const mapMrcGetCp52ToFormData = (apiResult, base = {}) => {
  const d = unwrapMrcCp52Record(apiResult);
  const mergedBase = buildCPForm52OfMRCData(base);
  const refParts = parseCp52ReferenceNo(
    pick(d, ["REFERENCE_NO", "referenceNo", "REF_NO"]) || ""
  );

  const { amountCharged, amountToBeCharged, amountToBeAdjusted } =
    buildChargeRowsFromRecord(d, mergedBase.chargeCols);

  const adjNumber = asText(
    pick(d, [
      "TRACKING_ID",
      "RO_ADJ_NO",
      "ADJUSTMENT_NO",
      "oracleTrackingId",
      "adjNumber",
    ]) ?? mergedBase.adjNumber
  ).replace(/\.0$/, "");

  const mapped = {
    ...mergedBase,
    batchNo: refParts.batchNo || mergedBase.batchNo,
    subDivision:
      refParts.subDivision ||
      asText(
        pick(d, ["DIVCODE", "subDivision", "subDivisionCode"]) ??
          mergedBase.subDivision
      ),
    accountNo:
      refParts.accountNo ||
      asText(
        pick(d, ["APPLICATION_NO", "accountNo", "customerId"]) ??
          mergedBase.accountNo
      ),
    adjC: asText(
      pick(d, ["ADJ_TYPE1", "adjC", "ADJ_C"]) ?? mergedBase.adjC ?? "A"
    ),
    adjNumber: adjNumber || mergedBase.adjNumber,
    adjDate: formatCp52Date(
      pick(d, ["RO_ADJ_DATE", "ADJUSTMENT_DATE", "adjDate"]) ??
        mergedBase.adjDate
    ),
    dueDate: formatCp52Date(
      pick(d, ["DUE_DT", "dueDate", "DUE_DATE"]) ?? mergedBase.dueDate
    ),
    readingDate: formatCp52Date(
      pick(d, ["READING_DATE", "readingDate"]) ?? mergedBase.readingDate
    ),
    billingMonth: formatCp52BillingMonth(
      pick(d, ["B_PERIOD", "BILLING_MONTH", "billingMonth"]) ??
        mergedBase.billingMonth
    ),
    extendedDueDate: formatCp52Date(
      pick(d, ["EXTENDED_DUE_DT", "extendedDueDate"]) ??
        mergedBase.extendedDueDate
    ),
    tariffCode: asText(
      pick(d, ["TARIFF_ACTIVE", "tariffCode", "TARIFF_CODE"]) ??
        mergedBase.tariffCode
    ),
    tariffDisplay: asText(
      mergedBase.tariffDisplay ||
        resolveMrcTariffDisplay(
          pick(d, ["TARIFF_ACTIVE", "tariffCode", "TARIFF_CODE"]) ??
            mergedBase.tariffCode,
          mergedBase.tariffs || []
        )
    ),
    enqNo: asText(
      pick(d, ["EOPS_TRACKING_ID", "enqNo", "UI_APPLICATION_NO"]) ??
        mergedBase.enqNo
    ),
    enqDate: formatCp52Date(
      pick(d, ["CREATED_DATE", "enqDate", "RO_ADJ_DATE"]) ?? mergedBase.enqDate
    ),
    consumerName: asText(
      pick(d, ["consumerName", "CONSUMER_NAME", "applicantName"]) ??
        mergedBase.consumerName
    ),
    consumerAddress: asText(
      pick(d, ["consumerAddress", "CONSUMER_ADDRESS", "address"]) ??
        mergedBase.consumerAddress
    ),
    chargeCols: mergedBase.chargeCols,
    amountCharged,
    amountToBeCharged,
    amountToBeAdjusted,
    totalAdjustment: formatCp52Amount(
      pick(d, ["AMOUNT", "CALCULATED_AMOUNT", "totalAdjustment"]) ??
        mergedBase.totalAdjustment
    ),
    noOfMonths: asText(
      pick(d, ["N_MONTH", "noOfMonths", "NO_OF_MONTHS"]) ??
        mergedBase.noOfMonths
    ),
    meterNo: asText(
      pick(d, [
        "meterNo",
        "METER_NO",
        "meterSerialNo",
        "METER_SERIAL_NO",
        "meterSerialNumber",
        "serialNo",
      ]) ?? mergedBase.meterNo
    ),
    setNo: asText(pick(d, ["setNo", "SET_NO", "set_no"]) ?? mergedBase.setNo),
    previousReading: asText(
      pick(d, [
        "previousReading",
        "PREVIOUS_READING",
        "previousReadCharged",
        "PREVIOUS_READ_CHARGED",
        "PREV_READING",
        "PREV_READ",
      ]) ?? mergedBase.previousReading
    ),
    presentAsCharged: asText(
      pick(d, [
        "presentAsCharged",
        "PRESENT_AS_CHARGED",
        "asCharged",
        "presentReadCharged",
        "PRESENT_READ_CHARGED",
        "METER_READING",
        "meterReading",
      ]) ?? mergedBase.presentAsCharged
    ),
    presentToBeCharged: asText(
      pick(d, [
        "presentToBeCharged",
        "PRESENT_TO_BE_CHARGED",
        "toBeCharged",
        "presentReadToBe",
        "PRESENT_READ_TO_BE",
        "authorityApprovedReadings",
        "AUTHORITY_APPROVED_READINGS",
        "authorityClaimedReading",
        "AUTHORITY_CLAIMED_READING",
      ]) ?? mergedBase.presentToBeCharged
    ),
    unitAdjusted: formatUnitsAdjDisplay(
      pick(d, [
        "KWH_ADJUSTED",
        "unitAdjusted",
        "unitsAdjusted",
        "UNITS_ADJUSTED",
      ]) ?? mergedBase.unitAdjusted,
      "0"
    ),
    unitCharged: asText(
      pick(d, [
        "unitCharged",
        "UNIT_CHARGED",
        "unitsCharged",
        "KWH_CHARGED",
        "PRESENT_UNITS_CHARGED",
        "presentUnitsCharged",
      ]) ?? mergedBase.unitCharged
    ),
    // Subsidies: FC-Sur ← DSSUR, AQA ← AQTA, AQTA2 ← AQTA2
    subsidyCharged: {
      fcSur: formatCp52Amount(d.DSSUR_CHRG_C ?? d.DSSUR_C),
      aqa: formatCp52Amount(d.AQTA_C),
      aqta2: formatCp52Amount(d.AQTA2_C),
    },
    subsidyToBeCharged: {
      fcSur: formatCp52Amount(d.DSSUR_CHRG_A ?? d.DSSUR_A),
      aqa: formatCp52Amount(d.AQTA_A),
      aqta2: formatCp52Amount(d.AQTA2_A),
    },
    subsidyToBeAdjusted: {
      fcSur: formatCp52Amount(d.DSSUR ?? d.DSSUR_CHRG),
      aqa: formatCp52Amount(d.AQTA),
      aqta2: formatCp52Amount(d.AQTA2),
    },
  };

  return buildCPForm52OfMRCData(mapped);
};

/**
 * Map mrc/getCP52 → Advance Payment consumer bill (Bill Info + Meter Info).
 * Uses the same getCP52 + CP52 form mapping as the CP52 dialog.
 */
export const mapMrcGetCp52ToBillData = (apiResult, options = {}) => {
  const d = unwrapMrcCp52Record(apiResult);
  if (!d || typeof d !== "object" || Object.keys(d).length === 0) {
    return null;
  }

  const formData = mapMrcGetCp52ToFormData(apiResult, {
    ...(options.base || {}),
    tariffs: options.tariffs || options.base?.tariffs || [],
  });
  const referenceRaw =
    pick(d, ["REFERENCE_NO", "referenceNo", "REF_NO", "UI_REF_NO"]) || "";
  const cleanRef = String(referenceRaw).replace(/\s/g, "");
  const referenceNo =
    cleanRef.length >= 14
      ? `${cleanRef.slice(0, 2)} ${cleanRef.slice(2, 7)} ${cleanRef.slice(7)}`
      : cleanRef || null;

  // PAYABLE AMOUNT = payableWithinDueDate ± Adjustment (AMOUNT)
  // AMOUNT > 0 → add; AMOUNT < 0 → subtract
  const cp52AmountRaw =
    pick(d, ["AMOUNT", "CALCULATED_AMOUNT", "totalAdjustment"]) ??
    formData.totalAdjustment;
  const cp52AmountNum =
    Number(
      String(cp52AmountRaw ?? "")
        .replace(/,/g, "")
        .trim()
    ) || 0;
  const appPayableRaw =
    options.payableWithinDueDate ??
    options.base?.payableWithinDueDate ??
    options.base?.amountPayable ??
    options.base?.payableAmount;
  const appPayableNum = Number(
    String(appPayableRaw ?? "")
      .replace(/,/g, "")
      .trim()
  );
  const payableRounded = Number.isFinite(appPayableNum)
    ? computeMrcBillPayableAmount(appPayableNum, cp52AmountNum)
    : Math.round(cp52AmountNum);
  const payable = formatCp52Amount(payableRounded);
  const adjustmentDisplay =
    formData.totalAdjustment || formatCp52Amount(cp52AmountRaw);
  // L.P. surcharge: next 3 working days = 5%, after that = 10%
  const lpFive = Math.round(Math.abs(payableRounded) * 0.05);
  const lpTen = Math.round(Math.abs(payableRounded) * 0.1);
  // When payable is negative, LPS (and after-due totals) stay negative too
  const surchargeSign = payableRounded < 0 ? -1 : 1;
  const payableWithFive = formatCp52Amount(
    payableRounded + surchargeSign * lpFive
  );
  const payableWithTen = formatCp52Amount(
    payableRounded + surchargeSign * lpTen
  );
  const lpSurchargeFive = formatCp52Amount(surchargeSign * lpFive);
  const lpSurchargeTen = formatCp52Amount(surchargeSign * lpTen);
  const trackingId = asText(
    pick(d, ["TRACKING_ID", "RO_ADJ_NO", "ADJUSTMENT_NO"]) || formData.adjNumber
  ).replace(/\.0$/, "");

  const dueDateDisplay = formData.dueDate || null;
  const surchargeWindowDate =
    getMrcSurchargeWindowDate(dueDateDisplay) || dueDateDisplay;

  // Meter Info — same fields CP52 MRC meter band shows
  const previousReading =
    asText(
      formData.previousReading ||
        options.base?.previousReading ||
        pick(d, [
          "previousReading",
          "PREVIOUS_READING",
          "previousReadCharged",
          "PREVIOUS_READ_CHARGED",
          "PREV_READING",
          "PREV_READ",
        ])
    ) || null;
  const presentReading =
    asText(
      formData.presentToBeCharged ||
        pick(d, [
          "presentToBeCharged",
          "PRESENT_TO_BE_CHARGED",
          "authorityApprovedReadings",
          "AUTHORITY_APPROVED_READINGS",
        ])
    ) || null;
  // UNITS = (PRESENT − PREVIOUS) × MF — only when Present exists
  const units = (() => {
    if (!hasMrcPresentReading(presentReading)) return null;
    const present = Number(String(presentReading ?? "").replace(/,/g, "").trim());
    const previous = Number(String(previousReading ?? "").replace(/,/g, "").trim());
    if (!Number.isFinite(present) || !Number.isFinite(previous)) return null;
    return String(present - previous);
  })();
  const meterNo =
    asText(
      formData.meterNo ||
        pick(d, ["meterNo", "METER_NO", "meterSerialNo", "METER_SERIAL_NO"])
    ) || null;
  const meterFactor =
    asText(
      options.meterFactor ||
        formData.mf ||
        formData.meterFactor ||
        pick(d, ["MF", "mf", "meterFactor", "METER_FACTOR"])
    ) || null;

  const branding = getDiscoBillBranding(cleanRef || null);
  const meterInfoRows = buildMrcBillMeterInfoRows(
    options.documents || [],
    formData
  );
  const meterInfoTable = buildMrcBillMeterInfoTable(
    options.documents || [],
    formData
  );
  const meterImageUrlsResult = buildMrcBillMeterImages(options.documents || []);
  const meterImageUrls = meterImageUrlsResult.urls || [];
  const primaryMeter = meterInfoRows[0] || null;

  return {
    ...dummyAdvancePaymentBillData,
    ...branding,
    referenceNo,
    consumerId:
      asText(
        options.customerId ||
          options.base?.customerId ||
          options.base?.consumerId ||
          pick(d, ["customerId", "CUSTOMER_ID", "consumerId", "CONSUMER_ID"]) ||
          formData.accountNo ||
          pick(d, ["APPLICATION_NO"])
      ) || null,
    consumerName: formData.consumerName || null,
    address: formData.consumerAddress || null,
    billMonth: formatCp52BillingMonth(formData.billingMonth) || null,
    dueDate: dueDateDisplay,
    readingDate:
      formatCp52Date(
        formData.readingDate || options.base?.readingDate
      ) || null,
    issueDate:
      formatCp52Date(pick(d, ["PRINTED_DATE", "RO_ADJ_DATE"])) ||
      formData.adjDate ||
      formData.dueDate ||
      null,
    applicationId:
      options.applicationId ||
      pick(d, ["UI_APPLICATION_NO", "applicationId"]) ||
      formData.enqNo ||
      null,
    tariffCode: formData.tariffCode || null,
    tariff:
      resolveMrcTariffCode(
        formData.tariffCode || formData.tariff,
        options.tariffs || []
      ) ||
      asText(formData.tariffCode) ||
      null,
    category:
      resolveMrcCategoryFromTariff(
        formData.tariffCode || formData.tariff,
        options.tariffs || []
      ) ||
      asText(formData.connectionType || formData.consumerType) ||
      null,
    tariffCategory:
      asText(
        options.base?.tariffDisplay ||
          formData.tariffDisplay ||
          resolveMrcTariffDisplay(
            formData.tariffCode || formData.tariff,
            options.tariffs || []
          )
      ) || null,
    subDivision: formData.subDivision || null,
    subDivisionName: formData.subDivision || null,
    // Meter Info — FESCO table (all CP52 sets)
    meterInfoTable,
    meterInfoRows,
    meterNo: meterInfoTable?.meterNo || primaryMeter?.meterNo || meterNo,
    meterFactor: primaryMeter?.meterFactor || meterFactor,
    previousReading: primaryMeter?.previousReading || previousReading,
    presentReading: primaryMeter?.presentReading || presentReading,
    units: primaryMeter?.units || units,
    meterNote: null,
    // KWH photos: site evidence first, else consumer claimed
    meterImageUrls,
    meterImageUrl: meterImageUrls[0] || null,
    meterImagePdfs: meterImageUrlsResult.pdfUrls || [],
    meterImageSource: meterImageUrlsResult.source,
    totalElectricityCharges: null,
    netElectricityCharges: null,
    taxes: null,
    subsidies: null,
    arrears: null,
    installment: null,
    adjustments: adjustmentDisplay,
    currentBill: payable,
    grandTotal: payable,
    // Sidebar + slip: payableWithinDueDate − getCP52 AMOUNT
    payableWithinDueDate: payable,
    payableAmount: payable,
    cp52Amount: formatCp52Amount(cp52AmountNum),
    applicationPayableWithinDueDate: Number.isFinite(appPayableNum)
      ? formatCp52Amount(appPayableNum)
      : null,
    lpSurcharge: lpSurchargeFive,
    lpSurchargeAfter: lpSurchargeTen,
    payableAfterDueDateTill: payableWithFive,
    payableAfterDueDateAfter: payableWithTen,
    // Due Date module: +3 working days window for 5% / after = 10%
    surchargeTillDate: surchargeWindowDate,
    surchargeAfterDate: surchargeWindowDate,
    barcodeRaw: trackingId || cleanRef || null,
    barcodeValue: trackingId || cleanRef || null,
    barcodeCaption: trackingId || null,
    billMessagesLayoutOnly: true,
    hideHeaderUrduText: false,
    hideQrContent: payableRounded < 0,
    raastQr:
      payableRounded < 0
        ? null
        : apiResult?.RAASTQR || apiResult?.raastQr || null,
  };
};
